import React, { useState, useEffect, useRef } from 'react'
import { css } from '@emotion/react'
import ReactDOM from 'react-dom'
import { checkIsSafari } from '@/utils/checkIsSafari'

interface Props {
  text: string
  width?: string | number
}

export const EllipsisWithTooltipOnly = ({ text, width = 200 }: Props) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const timeout = setTimeout(() => {
      const el = containerRef.current
      if (el && text.trim() !== '') {
        const isOverflowing = el.scrollHeight > el.offsetHeight + 1
        setShouldShowTooltip(isOverflowing)
      }
    }, 0)

    return () => clearTimeout(timeout)
  }, [text, width])

  const handleMouseEnter = () => {
    if (!checkIsSafari() && shouldShowTooltip && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      })
      setShowTooltip(true)
    }
  }

  const handleMouseLeave = () => {
    setShowTooltip(false)
  }

  return (
    <>
      <div
        css={baseStyle(width)}
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {text}
      </div>

      {showTooltip &&
        ReactDOM.createPortal(
          <div
            className="full-text-popup"
            css={popupStyle}
            style={{ top: position.top, left: position.left }}
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  )
}

const baseStyle = (width: string | number) =>
  css({
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    cursor: 'default',
    lineHeight: '18px',
    minHeight: '18px',
    height: 'auto',
    maxWidth: width,
    width,
  })

const popupStyle = css({
  position: 'absolute',
  zIndex: 10000,
  background: 'white',
  marginTop: '-1px',
  marginLeft: '5px',
  padding: '5px 10px',
  boxShadow: '2px 4px 7px rgba(0, 0, 0, 0.15)',
  border: '1px solid #ccc',
  maxWidth: '700px',
  whiteSpace: 'normal',
  wordBreak: 'break-word',

  '@media(max-width: 1150px)': {
    maxWidth: '400px !important',
  },

  color: '#000',
  fontFamily: 'Open Sans',
  fontSize: '13px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
})
